import { ChevronRightIcon, ChevronUpIcon, TrashIcon } from "@primer/octicons-react";
import { Box, IconButton } from "@primer/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { saleSetApi } from "../../../api/saleSetApi";
import { showNotify } from "../../../helpers/toast";
import { sf_saleset_viewmodel } from "../../../models/response/sale-set/sf_saleset";
import MyModal from "../../../components/ui/modal/MyModal";
import { useCommonContext } from "../../../contexts/common";
import ModalActions from "../../../components/ui/modal/ModalActions";
import Button from "../../../components/ui/button";
import DataGrid from "../../../components/data-grid";
import { IDataGridColumn } from "../../../components/data-grid/type/IDataGridProps";
import FocusedCell from "../../../components/data-grid/FocusedCell";
import TextInputNumber from "../../../components/text-input-number/TextInputNumber";

interface CongNoModalProps {
  isOpen: boolean;
  onClose: () => void;
  ts_hocsinh_id: number;
  nam_hoc: string;
  ho_ten?: string;
}

const CongNoModal: React.FC<CongNoModalProps> = ({
  isOpen,
  onClose,
  ts_hocsinh_id,
  nam_hoc,
  ho_ten,
}) => {
  const { translate } = useCommonContext();
  const [dataSource, setDataSource] = useState<sf_saleset_viewmodel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [focusedCell, setFocusedCell] = useState<any>();
  const [changeData, setChangeData] = useState<any[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Map<string, string>>(
    new Map()
  );

  const toggleGroup = useCallback((groupKey: string) => {
    setExpandedGroups((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(groupKey)) {
        newMap.delete(groupKey);
      } else {
        newMap.set(groupKey, groupKey);
      }
      return newMap;
    });
  }, []);

  const loadData = useCallback(async () => {
    if (!ts_hocsinh_id || !nam_hoc) return;

    setIsLoading(true);
    try {
      const res = await saleSetApi.selectByStudent({
        nam_hoc,
        ts_hocsinh_id,
      });

      if (res.is_success && res.data) {
        const data = res.data;
        if (Array.isArray(data) && data.length > 0) {
          setDataSource(data);
        } else {
          setDataSource([]);
        }
        setChangeData([]);
      } else {
        showNotify({ message: "Không thể tải dữ liệu công nợ", type: "warning" });
        setDataSource([]);
        setChangeData([]);
      }
    } catch (error) {
      console.error("Error loading cong no:", error);
      showNotify({ message: "Lỗi khi tải dữ liệu công nợ", type: "error" });
      setDataSource([]);
      setChangeData([]);
    } finally {
      setIsLoading(false);
    }
  }, [nam_hoc, ts_hocsinh_id]);

  useEffect(() => {
    if (isOpen && ts_hocsinh_id && nam_hoc) {
      loadData();
    }
  }, [isOpen, ts_hocsinh_id, nam_hoc, loadData]);

  const onUpdated = useCallback(
    (rowId: number, dataField: string, value: any) => {
      if (dataField !== "so_tien_dinh_muc") return;

      const row = dataSource.find((x: any) => x.id === rowId);
      const canEditRow = (row?.so_tien_da_nop ?? 0) === 0;
      if (!canEditRow) return;

      setDataSource((pre: any[]) => {
        return pre.map((x: any) => {
          if (x.id === rowId) {
            const item: any = { ...x };
            item[dataField] = value;
            if (dataField === "so_tien_dinh_muc") {
              const so_tien_dinh_muc = item.so_tien_dinh_muc ?? 0;
              const so_tien_mien_giam = item.so_tien_mien_giam ?? 0;
              const so_tien_phai_nop = so_tien_dinh_muc - so_tien_mien_giam;
              item.so_tien_phai_nop = so_tien_phai_nop;
              item.so_tien_thua_thieu = (item.so_tien_da_nop ?? 0) - so_tien_phai_nop;
            }
            return item;
          }
          return x;
        });
      });

      setChangeData((prev) => {
        const existed = prev.find((x) => x.id === rowId && x.dataField === dataField);
        if (existed) return prev;
        return [...prev, { id: rowId, dataField, value }];
      });
    },
    [dataSource]
  );

  const handleSubmit = useCallback(async () => {
    if (!nam_hoc || !ts_hocsinh_id) {
      showNotify({ message: "Thiếu thông tin năm học hoặc học sinh", type: "error" });
      return;
    }

    if (changeData.length === 0) {
      showNotify({ message: "Không có thay đổi nào để lưu", type: "info" });
      return;
    }

    try {
      const changedItemIds = Array.from(new Set(changeData.map((x) => x.id)));
      const items = changedItemIds
        .map((itemId) => dataSource.find((x: any) => x.id === itemId))
        .filter((item) => item !== undefined)
        .map((item: any) => ({
          sf_khoannop_id: item.sf_khoannop_id,
          so_tien_dinh_muc: item.so_tien_dinh_muc || 0,
          ghi_chu: item.ghi_chu || "",
        }));

      const res = await saleSetApi.saveChanges({
        nam_hoc,
        ts_hocsinh_id,
        items,
      });

      if (res.is_success) {
        showNotify({ message: "Lưu thay đổi thành công", type: "success" });
        setChangeData([]);
        await loadData();
      } else {
        showNotify({ message: res.message || "Lưu thất bại", type: "error" });
      }
    } catch {
      showNotify({ message: "Có lỗi xảy ra khi lưu", type: "error" });
    }
  }, [changeData, dataSource, loadData, nam_hoc, ts_hocsinh_id]);

  const handleDelete = useCallback(
    async (row: any) => {
      if (row.isGroupHeader) return;
      if (!nam_hoc || !ts_hocsinh_id) {
        showNotify({ message: "Thiếu thông tin năm học hoặc học sinh", type: "error" });
        return;
      }
      if (Number(row.so_tien_da_nop || 0) !== 0) {
        showNotify({ message: "Chỉ được xóa khoản thu chưa thu tiền", type: "warning" });
        return;
      }

      try {
        setIsLoading(true);
        const res = await saleSetApi.saveChanges({
          nam_hoc,
          ts_hocsinh_id,
          items: [
            {
              sf_khoannop_id: row.sf_khoannop_id,
              so_tien_dinh_muc: 0,
              ghi_chu: row.ghi_chu || "",
            },
          ],
        });

        if (res.is_success) {
          showNotify({ message: "Xóa khoản thu thành công", type: "success" });
          await loadData();
        } else {
          showNotify({ message: res.message || "Không thể xóa khoản thu", type: "error" });
        }
      } catch {
        showNotify({ message: "Có lỗi xảy ra khi xóa khoản thu", type: "error" });
      } finally {
        setIsLoading(false);
      }
    },
    [loadData, nam_hoc, ts_hocsinh_id]
  );

  const flatDataWithGroups = useMemo(() => {
    const groups: { [key: string]: { group: string; items: sf_saleset_viewmodel[]; totals: any } } = {};

    const getOrder = (item: sf_saleset_viewmodel): { thuTuNhom: number; thuTu: number } => {
      return {
        thuTuNhom: Number(item.thutu_sapxep_nhomkhoannop) || 0,
        thuTu: Number(item.thutu_sapxep) || 0,
      };
    };

    dataSource.forEach((item) => {
      const groupKey = (item.sf_khoannop_group_id ?? "unknown").toString();
      if (!groups[groupKey]) {
        groups[groupKey] = {
          group: item.ten_khoan_nop_group || item.ma_khoan_nop_group || "Khác",
          items: [],
          totals: {
            total_dinh_muc: 0,
            total_mien_giam: 0,
            total_phai_nop: 0,
            total_da_nop: 0,
            total_hoan_phi: 0,
            total_thua_thieu: 0,
          },
        };
      }
      groups[groupKey].items.push(item);
      groups[groupKey].totals.total_dinh_muc += item.so_tien_dinh_muc || 0;
      groups[groupKey].totals.total_mien_giam += item.so_tien_mien_giam || 0;
      groups[groupKey].totals.total_phai_nop += item.so_tien_phai_nop || 0;
      groups[groupKey].totals.total_da_nop += item.so_tien_da_nop || 0;
      groups[groupKey].totals.total_hoan_phi += item.so_tien_hoan_phi || 0;
      groups[groupKey].totals.total_thua_thieu += item.so_tien_thua_thieu || 0;
    });

    const flatData: any[] = [];

    const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
      const ordersA = groups[a].items.map((x) => getOrder(x));
      const ordersB = groups[b].items.map((x) => getOrder(x));

      const minNhomA = Math.min(...ordersA.map((o) => o.thuTuNhom));
      const minNhomB = Math.min(...ordersB.map((o) => o.thuTuNhom));
      if (minNhomA !== minNhomB) return minNhomA - minNhomB;

      const minThuTuA = Math.min(...ordersA.map((o) => o.thuTu));
      const minThuTuB = Math.min(...ordersB.map((o) => o.thuTu));
      return minThuTuA - minThuTuB;
    });

    sortedGroupKeys.forEach((groupKey) => {
      const groupData = groups[groupKey];
      const isExpanded = expandedGroups.has(groupKey);

      const sortedItems = [...groupData.items].sort((x, y) => {
        const a = getOrder(x);
        const b = getOrder(y);
        if (a.thuTuNhom !== b.thuTuNhom) return a.thuTuNhom - b.thuTuNhom;
        if (a.thuTu !== b.thuTu) return a.thuTu - b.thuTu;
        return String(x.ma_khoan_nop || "").localeCompare(String(y.ma_khoan_nop || ""));
      });
      
      flatData.push({
        isGroupHeader: true,
        groupKey: groupKey,
        ten_khoan_nop_group: groupData.group,
        totals: groupData.totals,
        ma_khoan_nop: "",
        ten_khoan_nop: "",
        so_tien_dinh_muc: null,
        so_tien_mien_giam: null,
        so_tien_phai_nop: null,
        so_tien_da_nop: null,
        so_tien_hoan_phi: null,
        so_tien_thua_thieu: null,
        is_da_hach_toan: false,
        ghi_chu: "",
      });
      
      if (isExpanded) {
        sortedItems.forEach((item) => {
          flatData.push({
            ...item,
            isGroupHeader: false,
            parentGroup: groupKey,
          });
        });
      }
    });

    return flatData;
  }, [dataSource, expandedGroups]);

  useEffect(() => {
    if (dataSource.length > 0) {
      const newExpandedGroups = new Map<string, string>();
      const uniqueGroups = new Set<string>();
      
      dataSource.forEach((item) => {
        const groupKey = (item.sf_khoannop_group_id ?? "unknown").toString();
        uniqueGroups.add(groupKey);
      });

      uniqueGroups.forEach((groupKey) => {
        newExpandedGroups.set(groupKey, groupKey);
      });

      setExpandedGroups(newExpandedGroups);
    }
  }, [dataSource]);

  const columns = useMemo<IDataGridColumn[]>(() => {
    return [
      {
        caption: translate("Nhóm khoản nộp"),
        dataField: "ten_khoan_nop_group",
        width: 200,
        fixed: "left",
        cellRender: (data: any) => {
          const level = data.isGroupHeader ? 0 : 1;
          const isExpanded = expandedGroups.has(data.groupKey || data.parentGroup);

          if (data.isGroupHeader) {
            return (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  icon={!isExpanded ? ChevronRightIcon : ChevronUpIcon}
                  aria-label=""
                  variant="invisible"
                  size="small"
                  onClick={() => toggleGroup(data.groupKey)}
                />
                <Box
                  sx={{
                    fontSize: "13px",
                    fontWeight: "bold",
                    marginLeft: "4px",
                    color: "#0969da",
                  }}
                >
                  {data.ten_khoan_nop_group}
                </Box>
              </Box>
            );
          }
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: `${level * 30}px` }}>&nbsp;</Box>
              <span>{data.ten_khoan_nop_group || ""}</span>
            </Box>
          );
        },
      },
      {
        caption: translate("Mã khoản nộp"),
        dataField: "ma_khoan_nop",
        width: 130,
        fixed: "left",
        cellRender: (data: any) => {
          if (data.isGroupHeader) {
            return <span></span>;
          }
          const level = 1;
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: `${level * 30}px` }}>&nbsp;</Box>
              <span>{data.ma_khoan_nop}</span>
            </Box>
          );
        },
      },
      {
        caption: translate("Tên khoản nộp"),
        dataField: "ten_khoan_nop",
        width: 200,
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          const level = 1;
          return (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ width: `${level * 30}px` }}>&nbsp;</Box>
              <span>{data.ten_khoan_nop}</span>
            </Box>
          );
        },
      },
      {
        caption: translate("Định mức"),
        dataField: "so_tien_dinh_muc",
        width: 120,
        align: "right",
        isAllowFocus: true,
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          const canEdit = (data.so_tien_da_nop ?? 0) === 0;
          const isFocused =
            canEdit &&
            focusedCell &&
            focusedCell.dataField === "so_tien_dinh_muc" &&
            focusedCell.rowData.id === data.id;
          const value = data.so_tien_dinh_muc;
          return (
            <Box>
              {isFocused && (
                <FocusedCell onLostFocused={() => setFocusedCell(undefined)}>
                  <TextInputNumber
                    defaultValue={value}
                    autoFocus
                    className="text-input-noborder"
                    isAlignRight
                    onValueChanged={(v) => onUpdated(data.id, "so_tien_dinh_muc", v)}
                  />
                </FocusedCell>
              )}
              {!isFocused && <>{formatCurrency(value)}</>}
            </Box>
          );
        },
      },
      {
        caption: translate("Miễn giảm"),
        dataField: "so_tien_mien_giam",
        width: 120,
        align: "right",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return <span>{formatCurrency(data.so_tien_mien_giam)}</span>;
        },
      },
      {
        caption: translate("Phải nộp"),
        dataField: "so_tien_phai_nop",
        width: 120,
        align: "right",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return (
            <span>
              {formatCurrency(data.so_tien_phai_nop)}
            </span>
          );
        },
      },
      {
        caption: translate("Đã nộp"),
        dataField: "so_tien_da_nop",
        width: 120,
        align: "right",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return (
            <span>
              {formatCurrency(data.so_tien_da_nop)}
            </span>
          );
        },
      },
      {
        caption: translate("Đã hoàn trả"),
        dataField: "so_tien_hoan_phi",
        width: 120,
        align: "right",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return (
            <span>
              {formatCurrency(data.so_tien_hoan_phi)}
            </span>
          );
        },
      },
      {
        caption: translate("Thừa/Thiếu"),
        dataField: "so_tien_thua_thieu",
        width: 120,
        align: "right",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          const value = data.so_tien_thua_thieu || 0;
          const color = value > 0 ? "#1a7f37" : value < 0 ? "#d1242f" : "#666";
          return (
            <span style={{ color, fontWeight: "bold" }}>
              {formatCurrency(value)}
            </span>
          );
        },
      },
      {
        caption: translate("Đã hạch toán"),
        dataField: "is_da_hach_toan",
        width: 100,
        align: "center",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return data.is_da_hach_toan ? (
            <span style={{ color: "#1a7f37", fontSize: "16px", fontWeight: "bold" }}>✓</span>
          ) : (
            <span></span>
          );
        },
      },
      {
        caption: translate("Ghi chú"),
        dataField: "ghi_chu",
        width: 200,
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          return <span>{data.ghi_chu || ""}</span>;
        },
      },
      {
        caption: "",
        dataField: "cmd",
        width: 60,
        align: "center",
        cellRender: (data: any) => {
          if (data.isGroupHeader) return <span></span>;
          const canDelete = Number(data.so_tien_da_nop || 0) === 0;
          if (!canDelete) return <span></span>;
          return (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <IconButton
                icon={TrashIcon}
                aria-label={translate("Xóa khoản thu")}
                size="small"
                variant="invisible"
                onClick={() => handleDelete(data)}
              />
            </Box>
          );
        },
      },
    ];
  }, [expandedGroups, focusedCell, handleDelete, onUpdated, toggleGroup, translate]);

  const formatCurrency = (value: any) => {
    if (value === null || value === undefined || value === 0) return "";
    return new Intl.NumberFormat("vi-VN").format(value);
  };

  const totalSummary = useMemo(() => {
    return [
      { dataField: "so_tien_dinh_muc", summaryType: "sum" as const },
      { dataField: "so_tien_mien_giam", summaryType: "sum" as const },
      { dataField: "so_tien_phai_nop", summaryType: "sum" as const },
      { dataField: "so_tien_da_nop", summaryType: "sum" as const },
      { dataField: "so_tien_hoan_phi", summaryType: "sum" as const },
      { dataField: "so_tien_thua_thieu", summaryType: "sum" as const },
    ];
  }, []);

  return (
    <MyModal
      isOpen={isOpen}
      onClose={onClose}
      title={translate(`Danh sách khoản thu học sinh - ${ho_ten}`)}
      width="90%"
    >
      <Box sx={{ p: 1 }}>
          <DataGrid
            dataSource={flatDataWithGroups}
            columns={columns}
            height={window.innerHeight - 235}
            isLoading={isLoading}
            paging={{
              enable: false,
            }}
            searchEnable={false}
            exportConfig={{
              enable: false,
            }}
            focusedCell={{
              focusedCell,
              onFocusedCellChanged: setFocusedCell,
            }}
            totalSummary={totalSummary}
          />
      </Box>

      <ModalActions>
      <Button variant="default" onClick={onClose}>
          {translate("Đóng")}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isLoading || changeData.length === 0}
        >
          {translate("Lưu thay đổi")}
        </Button>
      
      </ModalActions>
    </MyModal>
  );
};

export default CongNoModal;
